import hash from 'object-hash';
import Vue, { VueConstructor } from 'vue';
import _ from 'lodash';
import { getEditRoute } from '@/main/router/routerPath';
import { EventBus } from '@/main/eventBus';
import { router } from '@/main/router';
import { PageMode } from '@/main/utils/pageMode';
import { EditStoreBase, LayoutPropsEdit } from '@/main/store/@types/edit.d';
import { Route } from 'vue-router';
import {
	SET_PROPS,
	START_LOADING, END_LOADING,
	CREATE,
	DEFAULT,
	DELETE,
	editName,
	ENTITY,
	PGE_MGR_EDIT_PAGE,
	RESET,
	TABLE_DATA,
	TABLE_SCHEMA,
	UPDATE,
	TABLE_SCHEMA_DATA, TABLE_DATA_EXPORT, TABLE_DATA_DELETE,
} from '@/main/store/listEditTypes';

import { Table, EditableStatus, Row } from '../utils/table';
import { Page, isRouteEvent, isBeforeUnloadEvent, RouteEvent } from './page';
import { storesMap } from '../store/registry';


type PageWithEditStore = InstanceType<typeof Page> & EditStoreBase;

/**
 * @abstract use `editFactory` to create Edit components.
 */
export const Edit = (Page as VueConstructor<PageWithEditStore>).extend({
	data() {
		return {
			dependencies: {},
		};
	},
	computed: {
		layoutProps(): LayoutPropsEdit {
			return {
				operations: this.operations,
				mode: this.pageMode,
				saveClick: this.saveClick,
				deleteClick: this.deleteClick,
				closeClick: this.closeClick,
				model: this.edit,
				validationRules: this.validationRulesCmp,
				isModified: this.isModified,
				isArchive: this.isArchive,
			};
		},
		isArchive(): boolean {
			const typedThis: Vue & { VyrazenDat: Date, VyrazenPrp: boolean } = this as any;
			return !_.isNil(typedThis.VyrazenDat) || (_.isNil(typedThis.VyrazenPrp) && typedThis.VyrazenPrp);
		},
		/** Returns validation rules for the entity that can be edited. */
		validationRulesCmp(): object {
			return this.validationRules ?? {};
		},
		/** Validator which validate values on form */
		validator() {
			return (this.$children[0].$refs.mainForm as any).validate;
		},
		mainForm(): Vue {
			return this.$children[0].$refs.mainForm as Vue;
		},
		isFormDataChanged(): boolean {
			return hash(this.edit) !== this.editHash;
		},
		/**
		 * Returns true if the page is an editable form which has been modified.
		 */
		isModified(): boolean {
			return this.isEditForm && hash(this.edit) !== this.editHash;
		},
		/**
		 *  Define if the form have Zpracuj mode. This mode is specific on data state. The programmer have to override and define when it’s true.
		 */
		isZpracujMode(): boolean {
			return false;
		},
		dependenciesById(): string[] {
			return _.filter(this.dependencies, (key) => this.dependentTables[key].loadByEntityId);
		},
		dependeciesByFilter(): string[] {
			return _.filter(this.dependencies, (key) => !this.dependentTables[key].loadByEntityId);
		},
		tableListeners(): {} {
			return {
				refresh: this.refreshTable,
				export: this.exportTable,
				'sort-change': this.saveSort,
			};
		},
	},
	watch: {
		isZpracujMode(zpracuj) {
			EventBus.$emit('DisableAll', zpracuj);
		},
	},
	methods: {
		/**
		 * Refreshes component table data by calling {@link this.fetchTableData}.
		 *
		 * Called when component is navigated to by closing another component.
		 */
		saveSort(sort: { columnId: any; order: string }, tableName: string) {
			this.$store.commit(`${this.storeUri}/SAVE_SORT`, { data: { ...sort }, tableName });
		},
		refreshFun() {
			return this.fetchTableData();
		},
		async refreshTable(name: string) {
			await this.$store.dispatch(`${this.storeUri}/${name}_${TABLE_DATA}`, { filter: (this as any)[`${name}Filter`] });
		},
		async exportTable(name: string) {
			await this.$store.dispatch(`${this.storeUri}/${name}_${TABLE_DATA_EXPORT}`);
		},
		recomputeHash() {
			this.editHash = hash(this.edit);
		},
		async loadingWrapper(method: () => Promise<any>) {
			try {
				this.loadingCount += 1;
				await method();
			} finally {
				this.loadingCount -= 1;
			}
		},
		/** overwritten to use wrapped loadData method which ensures loading overlay disappears AFTER ALL the data is loaded and prepared */
		async storeToPageManager() {
			if (this.pageMgrPage?.loadData) {
				this.isExternal && (this.$store.commit(`${this.storeUri}/${SET_PROPS}`, { filter: this.externalOpener?.extraParams }));
				await this.loadingWrapper(this.loadData);
				this.pageMgrPage.loadData = false;
				this.pageMgrPage.path = this.$route.path;
				this.pageMgrPage.id = Number(this.$route.params.id);
			}
			if (this.pageMgrPage?.refresh) {
				await this.loadingWrapper(this.refreshFun);
			}
		},
		/** overwritten for id setting */
		async baseCreated() {
			this.entityId = this.$route.params.id; // TODO: is this a duplicate of `this.pageMgrPage.id`?
			await this.storeToPageManager();
		},
		/**
		 * Called when the check button is clicked, save the record.
		 */
		async saveClick() {
			await this.save();
		},
		/** Extend validation of custom validations (Mainly for possibility for user confirmation if it is ok or not) */
		async customValidation() {
			return true;
		},
		/** Form data validation */
		async validate(dialog = true) {
			let isEditValid = false;
			this.validator((isValid?: boolean) => {
				if (isValid) {
					isEditValid = true;
				} else if (dialog) {
					this.showErrorAlert({ message: this.LocalizeChyboveHlasky('Invalid_Form') });

					this.dmsTabs.forEach((tabs: any) => tabs.openTabWithErrors());
				}
			});

			return isEditValid && this.customValidation();
		},
		/**
		 * Save the record, creates or updates the entity depending on mode (CREATE or not).
		 *
		 * @param goBack = true -  go back to previous page.
		 */
		async save(goBack = true) {
			const isValid = await this.validate();
			if (!isValid) {
				return new Error('Data are not valid');
			}
			return (this.isCreateMode) ? this.create(goBack) : this.update(goBack);
		},
		/** Called when the trash can button is clicked, deletes the entity. */
		deleteClick() {
			this.delete();
		},
		/** Commits {@link RESET} mutation on this component's state in store. */
		resetStore() {
			this.$store.commit(
				RESET,
				{ storeModule: this.storeModule, storeObject: editName(this.name) },
			);
		},

		// #region create
		/**
		 * Dispatches CREATE action and sets `this.refreshPreviousPage`.
		 *
		 * @param goBack = true - go back to previous page.
		 * @param refreshPreviousPage = true.
		 */
		create(goBack = true, refreshPreviousPage = true): Promise<any> {
			this.refreshPreviousPage = refreshPreviousPage;
			return this.$store.dispatch(`${this.storeUri}/${CREATE}`, { goBack });
		},
		// #endregion

		// #region update
		/**
		 * Dispatches the UPDATE action and sets `this.refreshPreviousPage`.
		 *
		 * @param goBack = true - go back to previous page.
		 * @param refreshPreviousPage = true.
		 *
		 * @returns whether no error occurred.
		 */
		async update(goBack = true, refreshPreviousPage = true): Promise<boolean> {
			this.refreshPreviousPage = refreshPreviousPage;
			if (goBack && (!this.isFormDataChanged && !this.isArchive)) {
				router.back();

				return true;
			}

			return this.$store.dispatch(`${this.storeUri}/${UPDATE}`, { id: this.entityId, goBack });
		},
		// #endregion

		// #region delete
		/**
		 * Dispatches the DETELE action.
		 *
		 * @param refreshPreviousPage = true.
		 */
		delete(refreshPreviousPage = true) {
			this.refreshPreviousPage = refreshPreviousPage;
			return this.$store.dispatch(`${this.storeUri}/${DELETE}`, { id: this.entityId, goBack: true });
		},
		// #endregion

		// #region default
		/**
		 * Dispatches the DEFAULT action with {@link this.filter} as an argument.
		 *
		 * @param filter = null.
		 */
		default(filter: object | null = null) {
			return this.$store.dispatch(`${this.storeUri}/${DEFAULT}`, filter ?? this?.props?.filter ?? {});
		},
		// #endregion

		// #region entity
		/**
		 * Dispatches the ENTITY action.
		 */
		entity() {
			if (this.entityId === 'undefined' || this.entityId === undefined || this.entityId === null) {
				return this.showErrorAlert({ message: this.LocalizeChyboveHlasky('InvalidData') });
			} return this.$store.dispatch(`${this.storeUri}/${ENTITY}`, { id: this.entityId, edit: this.pageMode === PageMode.EDIT });
		},
		// #endregion

		/** If mode equals CREATE, calls {@this.default}, else calls {@this.entity}. */
		async loadData() {
			if (this.isCreateMode) {
				const resp = await this.default();
				if (resp instanceof Error) {
					this.$router.back();
				}
			} else {
				await this.entity();
				this.fetchTableData(this.dependeciesByFilter);
			}
			await this.loadedData();
			let schemaDependecies = this.dependeciesByFilter;
			if (this.isEditMode || this.isViewMode) {
				this.fetchTableSchemaData(this.dependenciesById);
			} else {
				schemaDependecies = [...this.dependeciesByFilter, ...this.dependenciesById];
			}
			this.fetchTableSchemas(schemaDependecies);
		},
		/** Do after load */
		async loadedData() { },
		/** refreshing data for page */
		async refreshPage() {
			await (this.isCreateMode ? this.default() : this.entity());
			await this.fetchTableData();
		},
		async changeUrlToEdit(id: string | number) {
			const { path: oldPath } = this.$route;

			await this.$router.replace(getEditRoute(this.name, id));

			this.entityId = id.toString();
			this.saveProps();
			EventBus.$emit('Mode', this.pageMode);

			this.$store.commit(
				`pageManager/${PGE_MGR_EDIT_PAGE}`,
				{ oldPath, page: { id, path: this.$route.path } },
				{ root: true },
			);
		},
		/** change create mode to edit */
		async changeToEdit(id: any = null) {
			this.loadingCount += 1;

			try {
				let respId = id;
				if (!respId) {
					const resp = await this.save(false);
					if (resp instanceof Error) { return resp; }
					if (!resp.headers || !resp.headers.location) return null;

					[, respId] = resp.headers.location.split('=');
				}
				await this.changeUrlToEdit(respId);
				await this.refreshPage();

				return respId;
			} catch (err) {
				console.error('Change to edit ERROR:', err);
				return err;
			} finally {
				this.loadingCount -= 1;
			}
		},
		/** Change mode to create. */
		async changeToCreate(filter: object) {
			this.loadingCount += 1;

			this.resetState();
			await this.default(filter);

			await this.$router.replace(getEditRoute(this.name, null, PageMode.CREATE));

			this.pageMgrPage!.loadData = false;
			this.pageMgrPage!.path = this.$route.path;
			this.pageMgrPage!.id = null;

			this.props.id = undefined;
			this.saveProps();

			this.loadingCount -= 1;
		},
		/**
		 * Reset edit and localState in store to initial values
		 */
		resetState() {
			const storeInit = storesMap.get(this.$options.name);
			const { edit, localState } = storeInit.state();
			this.edit = _.cloneDeep(edit);
			this.localState = _.cloneDeep(localState);
		},
		/**
		 * Loads schemas and data of dependent tables by dispatching the TABLE_SCHEMA action for
		 * each table.
		 *
		 * @returns array of table schemas.
		 */
		async fetchTableData(tablesToFetch?: string[]): Promise<any> {
			const tables: string[] = tablesToFetch ?? this.dependencies as string[];

			return Promise.all(
				_.map(tables, (tableName) => this.$store.dispatch(`${this.storeUri}/${tableName}_${TABLE_DATA}`)),
			);
		},
		/**
		 * Loads schemas of dependent tables by dispatching the TABLE_SCHEMA action for
		 * each table.
		 *
		 * @returns array of table schemas.
		 */
		async fetchTableSchemas(tablesToFetch?: string[]) {
			const tables: string[] = tablesToFetch ?? this.dependencies as string[];

			return Promise.all(
				_.map(tables, (tableName) => this.$store.dispatch(`${this.storeUri}/${tableName}_${TABLE_SCHEMA}`)),
			);
		},
		/**
		 * Loads data of dependent tables by dispatching the TABLE_DATA action for
		 * each table.
		 *
		 * @param tablesToFetch = Object.values(this.dependencies).
		 *
		 * @returns array of table rows.
		 */
		async fetchTableSchemaData(tablesToFetch?: string[]): Promise<any> {
			const tables: string[] = tablesToFetch ?? this.dependencies as string[];

			return Promise.all(
				_.map(tables, (tableName) => this.$store.dispatch(`${this.storeUri}/${tableName}_${TABLE_SCHEMA_DATA}`)),
			);
		},

		/**
		 * Shows a custom dialog box for the user to confirm leaving the page, if it's a modified edit form.
		 * Does not show the dialog if EditHash has not yet been computed (is null, indicating error on form creation).
		 * @returns 'true' if user confirmed leaving or page is not a modified edit 'form', false otherwise
		 */
		async getConfirmationForUnsavedEditForm() {
			if (this.editHash !== null && this.isModified) {
				return this.showConfirm({
					message: this.LocalizeChyboveHlasky('PageLeaveMsg'),
					title: this.Localize('ShowMsg'),
					options: {
						confirmButtonText: this.LocalizeChyboveHlasky('PageLeaveLeaveBtn'),
						cancelButtonText: this.LocalizeChyboveHlasky('PageLeaveStayBtn'),
						type: 'warning',
					},
				});
			}
			return true;
		},

		async shouldDestroyPage(to: Route) {
			return (!this.shouldRemoveFromPageManager(to) || this.getConfirmationForUnsavedEditForm());
		},

		async beforeUnload(event: BeforeUnloadEvent | RouteEvent | null = null) {
			await this.doBeforeUload(event);
			if (isRouteEvent(event)) {
				const goNext = await this.shouldDestroyPage(event.to);
				goNext && await this.handleRemovingFromPageManager(event.to);
				event.next(goNext ? undefined : false);
				return;
			}
			if (isBeforeUnloadEvent(event)) {
				if (this.isModified) {
					event.preventDefault();
					event.returnValue = this.LocalizeChyboveHlasky('PageLeaveMsg');
				}
			}
		},

		updateRow(rowUpdateData: { tableName: string, updateStatus: EditableStatus, rowId: number, row: Row }) {
			this.$store.commit(`${this.storeUri}/updateTableRow`, rowUpdateData);
		},

		updateRows({ tableName, rows }: { tableName: string, rows: Row[] }) {
			const table: Table = _.get(this.dependentTables, tableName);
			table && (table.rows = rows);
		},

		async deleteRowClick({ name, data }: { name: string, data: { ID: number } }) {
			this.$store.commit(`${this.storeUri}/${START_LOADING}`, name);
			try {
				await this.$store.dispatch(`${this.storeUri}/${name}_${TABLE_DATA_DELETE}`, { id: data.ID });
			} finally {
				this.$store.commit(`${this.storeUri}/${END_LOADING}`, name);
			}
		},
	},
});
